import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import PasswordInput from "../components/PasswordInput";
import PasswordStrength from "../components/PasswordStrength";
import api from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, KeyRound } from "lucide-react";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Reset link is missing a token");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      setDone(true);
      toast.success("Password updated — please sign in with your new password");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed — request a new link");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container-narrow py-16">
        <div className="mb-8">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary mb-3">
            <KeyRound size={22} />
          </div>
          <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight"
              data-testid="reset-password-title">
            Set a new password
          </h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            Choose a strong password (at least 8 characters). Once set, this link
            can't be used again.
          </p>
        </div>

        <Card className="mano-card">
          <CardContent className="p-6 md:p-8">
            {done ? (
              <div className="text-center py-4 space-y-3" data-testid="reset-password-done">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <ShieldCheck size={28} />
                </div>
                <div className="font-display font-semibold text-lg">Password updated</div>
                <p className="text-sm text-muted-foreground">
                  You can now sign in with your new password.
                </p>
                <div className="pt-3">
                  <Button onClick={() => nav("/login")}
                          className="rounded-full px-8 mano-lift"
                          data-testid="reset-password-to-login">
                    Sign in
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="rp-new">New password</Label>
                  <PasswordInput id="rp-new" required autoComplete="new-password" minLength={8}
                                 value={next} onChange={(e) => setNext(e.target.value)}
                                 data-testid="reset-password-new" />
                  <PasswordStrength value={next} testId="reset-password-strength" />
                </div>
                <div>
                  <Label htmlFor="rp-confirm">Confirm new password</Label>
                  <PasswordInput id="rp-confirm" required autoComplete="new-password" minLength={8}
                                 value={confirm} onChange={(e) => setConfirm(e.target.value)}
                                 data-testid="reset-password-confirm" />
                </div>
                <Button type="submit" size="lg" disabled={submitting || !token}
                        className="rounded-full w-full sm:w-auto px-10 mano-lift"
                        data-testid="reset-password-submit">
                  <ShieldCheck size={14} /> {submitting ? "Updating…" : "Update password"}
                </Button>
                <div className="text-sm text-muted-foreground pt-2">
                  Link expired?{" "}
                  <Link to="/forgot-password"
                        className="text-primary underline underline-offset-4"
                        data-testid="reset-password-request-new">
                    Request a fresh reset link
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
